import axios from 'axios';
import { acceptHMRUpdate, defineStore } from 'pinia';

export interface SportsCenter {
  id?: number;
  name: string;
  address: string;
  phone?: string | null;
  description?: string | null;
}

interface SportsCentersState {
  items: SportsCenter[];
  loading: boolean;
  error: string;
}

export const useSportsCentersStore = defineStore('sports_centers.store', {
  state: (): SportsCentersState => ({ items: [], loading: false, error: '' }),

  actions: {
    async fetchAll(): Promise<SportsCenter[]> {
      this.loading = true;
      this.error = '';
      try {
        const res = await axios.get('/api/v1/sports_centers');
        this.items = Array.isArray(res.data) ? res.data : [];
        return this.items;
      } catch (e: unknown) {
        const message =
          typeof e === 'object' && e && 'message' in e
            ? String((e as { message?: unknown }).message)
            : '';
        this.error = message || 'Error cargando centros deportivos';
        this.items = [];
        return [];
      } finally {
        this.loading = false;
      }
    },

    async create(data: SportsCenter): Promise<SportsCenter | null> {
      try {
        const res = await axios.post('/api/v1/sports_centers', { sports_center: data });
        const item = res.data as SportsCenter;
        this.items.push(item);
        return item;
      } catch (error) {
        console.error('Error creating sports center:', error);
        throw error;
      }
    },

    async update(id: number, data: Partial<SportsCenter>): Promise<SportsCenter | null> {
      try {
        const res = await axios.put(`/api/v1/sports_centers/${id}`, { sports_center: data });
        const updated = res.data as SportsCenter;
        const idx = this.items.findIndex((i) => i.id === id);
        if (idx !== -1) this.items[idx] = updated;
        return updated;
      } catch (error) {
        console.error('Error updating sports center:', error);
        throw error;
      }
    },

    async remove(id: number): Promise<void> {
      try {
        await axios.delete(`/api/v1/sports_centers/${id}`);
        this.items = this.items.filter((i) => i.id !== id);
      } catch (error) {
        console.error('Error deleting sports center:', error);
        throw error;
      }
    },
  },
});

// HMR support
if (import.meta && 'hot' in import.meta) {
  import.meta.hot?.accept(acceptHMRUpdate(useSportsCentersStore, import.meta as ImportMeta));
}
